import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useCallback, useState, createRef } from 'react'
import { useFormikContext, useField } from 'formik'
import { ISearch } from './typings'
import { LightTooltip } from '@sales-center/components'
import { inputBaseClasses } from '@mui/material/InputBase'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Chip from '@mui/material/Chip'
import SearchIcon from '@mui/icons-material/Search'

type Props = {
  disabled: boolean
}

const getStyles = (theme: Theme) => ({
  root: {
    height: '43px',
    cursor: 'pointer',
    marginRight: theme.spacing(1),
    paddingRight: theme.spacing(0.3),
    backgroundColor: theme.palette.common.white,
    width: '50px',
    transition: 'width 300ms',
  },

  input: { paddingRight: theme.spacing(0) },

  focused: {
    width: '300px',
    cursor: 'text',
  },

  icon: {
    cursor: 'pointer',
  },

  iconDisabled: {
    color: theme.palette.common.black,
    opacity: 0.26,
  },
})

export const SearchField = <T,>(props: Props) => {
  const { disabled } = props
  const theme = useTheme()
  const { icon, iconDisabled, ...styles } = getStyles(theme)
  const ref = createRef<HTMLInputElement>()

  const path = 'search'
  const valueField = `${path}.value`

  const [{ value: filter }] = useField<ISearch<T>>(path)
  const { placeholder } = filter

  const [text, setText] = useState('')

  const { setFieldValue, submitForm } = useFormikContext()
  const expanded = filter.value.length > 0 || text.length > 0
  //#region callbacks
  const onChangeText = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setText(target.value)
    },
    [],
  )

  const onKeyDown = useCallback(
    ({ key }: React.KeyboardEvent<unknown>) => {
      if (key === 'Enter') {
        ref.current?.blur()
        setFieldValue(valueField, text)
        setText('')
        submitForm()
      } else if (key === 'Escape') ref.current?.blur()
    },
    [ref, setFieldValue, submitForm, text, valueField],
  )

  const onClickSearch = useCallback(() => ref.current?.focus(), [ref])

  const onDeleteChip = useCallback(() => {
    setFieldValue(valueField, '')
    submitForm()
  }, [setFieldValue, submitForm, valueField])
  //#endregion

  return (
    <TextField
      disabled={disabled}
      inputRef={ref}
      value={text}
      onChange={onChangeText}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      color="primary"
      variant="outlined"
      size="small"
      data-test-id="searchInputField"
      InputProps={{
        sx: {
          ...styles.root,
          [`.${inputBaseClasses.input}`]: styles.input,
          ...(expanded && styles.focused),
          [`&.${inputBaseClasses.focused}`]: styles.focused,
        },

        startAdornment: (
          <InputAdornment position="start">
            <>
              <SearchIcon
                onClick={onClickSearch}
                sx={disabled ? iconDisabled : icon}
                data-test-id="searchIcon"
              />
              {!!filter.value && (
                <LightTooltip title={filter.value}>
                  <Chip
                    label={filter.value}
                    onDelete={onDeleteChip}
                    color="primary"
                    style={{
                      maxWidth: '150px',
                      marginLeft: theme.spacing(0.5),
                    }}
                    disabled={disabled}
                  />
                </LightTooltip>
              )}
            </>
          </InputAdornment>
        ),
      }}
    />
  )
}
